import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import FlagIcon from '@mui/icons-material/Flag';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import http from '../../services/http';

// Categorias no estilo Movidesk (item 1): cada uma com sua bandeirinha colorida.
const CATS = [
  { key: 'my_replied', label: 'Meus tickets respondidos', color: '#e53935' },
  { key: 'team_replied', label: 'Tickets da minha equipe respondidos', color: '#fb8c00' },
  { key: 'my_children_closed', label: 'Meus tickets com filhos encerrados', color: '#ec407a' },
  { key: 'transferred', label: 'Tickets transferidos para mim ou minha equipe', color: '#37474f' },
  { key: 'unassigned', label: 'Tickets abertos sem responsável', color: '#78909c' },
  { key: 'open_mine_team', label: 'Tickets abertos para mim ou minha equipe', color: '#b0bec5' },
];

const TicketAlertsPanel = () => {
  const { theme } = useContext(ThemeContext);
  const history = useHistory();
  const [anchorEl, setAnchorEl] = useState(null);
  // Categoria expandida no painel (accordion). null = todas recolhidas.
  const [expandedCat, setExpandedCat] = useState(null);
  const [data, setData] = useState({
    my_replied: [], team_replied: [], my_children_closed: [],
    transferred: [], unassigned: [], open_mine_team: [], counts: {},
  });
  const [loading, setLoading] = useState(false);
  const [statuses, setStatuses] = useState([]);
  // Menu de ações por ticket (item 50): âncora + ticket alvo.
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuTicket, setMenuTicket] = useState(null);
  // Diálogo de confirmação de exclusão.
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  // Ids já "vistos" (clicados) — o badge conta apenas os não vistos.
  const [seenIds, setSeenIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('ticketPanelSeen') || '[]')); }
    catch { return new Set(); }
  });

  const persistSeen = (set) => {
    try { localStorage.setItem('ticketPanelSeen', JSON.stringify([...set])); } catch { /* ignore */ }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await http.get('/notifications/ticket-panel');
      const d = res.data || {};
      setData(d);
      // Poda os "vistos" para manter só os que ainda aparecem (evita acúmulo e
      // faz um ticket que reaparece voltar a contar).
      const present = new Set(
        CATS.flatMap((c) => d[c.key] || []).map((t) => t.id)
      );
      setSeenIds((prev) => {
        const next = new Set([...prev].filter((id) => present.has(id)));
        persistSeen(next);
        return next;
      });
    } catch (e) {
      // silencioso; mantém dados anteriores
    } finally {
      setLoading(false);
    }
  }, []);

  // Lista de status para o menu "Aplicar status".
  useEffect(() => {
    http.get('/ticket-statuses')
      .then((res) => setStatuses(Array.isArray(res.data) ? res.data : []))
      .catch(() => setStatuses([]));
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const allItems = CATS.flatMap((c) => data[c.key] || []);
  const unseenTotal = allItems.filter((t) => !seenIds.has(t.id)).length;
  const open = Boolean(anchorEl);

  const markSeen = (id) => {
    setSeenIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      persistSeen(next);
      return next;
    });
  };

  // Marca vários como lidos de uma vez (categoria inteira ou todos).
  const markSeenMany = (ids) => {
    setSeenIds((prev) => {
      const next = new Set(prev);
      (ids || []).forEach((id) => next.add(id));
      persistSeen(next);
      return next;
    });
  };

  const openTicket = (id) => {
    markSeen(id);
    setAnchorEl(null);
    history.push(`/ticket/${id}`);
  };

  // Abre a listagem de tickets mostrando apenas os tickets desta categoria.
  // Abrir a listagem conta como "visto" — some o badge da categoria.
  const openInListingFor = (cat, catItems) => {
    markSeenMany(catItems.map((t) => t.id));
    setAnchorEl(null);
    history.push('/ticket', {
      ticketPanel: {
        key: cat.key,
        label: cat.label,
        ids: catItems.map((t) => t.id),
      },
    });
  };

  const openActionsMenu = (event, ticket) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuTicket(ticket);
  };

  const closeActionsMenu = () => {
    setMenuAnchor(null);
    setMenuTicket(null);
  };

  // Aplica um status ao ticket (ex.: "Cancelado" para spam).
  const applyStatus = async (status) => {
    const ticket = menuTicket;
    closeActionsMenu();
    if (!ticket) return;
    setBusy(true);
    try {
      await http.put(`/ticket/${ticket.id}`, { status_id: status.id });
      toast.success(`Ticket #${ticket.number}: status alterado para "${status.name}".`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.message || 'Não foi possível alterar o status.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // Exclui o ticket (após confirmação).
  const doDelete = async () => {
    const ticket = confirmDelete;
    if (!ticket) return;
    setBusy(true);
    try {
      await http.delete(`/ticket/${ticket.id}`);
      toast.success(`Ticket #${ticket.number} excluído.`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.message || 'Não foi possível excluir o ticket.';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Tooltip title="Painel de tickets" arrow>
        {/* Header é sempre escuro (var(--sidebar-shell-bg)); com color="inherit" o
            ícone herdava cor escura e sumia no tema claro. Precisa de branco COM
            !important no svg — há um CSS global do header que sobrescreve a cor do
            ícone (mesmo motivo do NotificationBadge). Item 19. */}
        <IconButton
          onClick={(e) => { setAnchorEl(e.currentTarget); load(); }}
          sx={{ color: 'white', '& svg': { color: 'white !important' } }}
        >
          <Badge badgeContent={unseenTotal} color="error">
            <MoveToInboxIcon sx={{ color: 'white' }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: '94vw', borderRadius: 2 } } }}
      >
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography sx={{ fontWeight: 700, color: theme.palette.text.primary }}>Painel de tickets</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{allItems.length} tickets</Typography>
            {unseenTotal > 0 && (
              <Tooltip title="Marcar todos como lidos" arrow>
                <IconButton size="small" onClick={() => markSeenMany(allItems.map((t) => t.id))} sx={{ p: 0.5 }}>
                  <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 460, overflowY: 'auto' }}>
          {loading ? (
            <Box sx={{ p: 2 }}><Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Carregando...</Typography></Box>
          ) : (
            CATS.map((c) => {
              const catItems = Array.isArray(data[c.key]) ? data[c.key] : [];
              const catUnseen = catItems.filter((t) => !seenIds.has(t.id)).length;
              const isOpen = expandedCat === c.key;
              return (
                <Box key={c.key}>
                  <ListItemButton
                    onClick={() => setExpandedCat(isOpen ? null : c.key)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.85 }}
                  >
                    <FlagIcon sx={{ fontSize: 18, color: c.color, flexShrink: 0 }} />
                    <Typography
                      variant="body2"
                      sx={{ flex: 1, minWidth: 0, color: theme.palette.text.primary, fontWeight: catUnseen ? 700 : 500 }}
                    >
                      {c.label}
                    </Typography>
                    {catUnseen > 0 && (
                      <Tooltip title="Marcar como lido" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); markSeenMany(catItems.map((t) => t.id)); }}
                          sx={{ flexShrink: 0, p: 0.25 }}
                        >
                          <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {catItems.length > 0 && (
                      <Tooltip title="Ver na listagem" arrow>
                        <IconButton
                          size="small"
                          onClick={(e) => { e.stopPropagation(); openInListingFor(c, catItems); }}
                          sx={{ flexShrink: 0, p: 0.25 }}
                        >
                          <OpenInNewIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Badge = NÃO-VISTOS da categoria, para a soma bater com o total
                        do ícone do header (que também conta só não-vistos). */}
                    {catUnseen > 0 && (
                      <Box
                        component="span"
                        sx={{
                          minWidth: 20, height: 20, px: 0.5, borderRadius: '10px',
                          fontSize: '0.7rem', fontWeight: 700, flexShrink: 0,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          color: '#fff',
                          bgcolor: theme.palette.error.main,
                        }}
                      >
                        {catUnseen}
                      </Box>
                    )}
                    {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </ListItemButton>
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    {catItems.length === 0 ? (
                      <Box sx={{ px: 3, py: 1 }}>
                        <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>Nenhum ticket nesta categoria.</Typography>
                      </Box>
                    ) : (
                      <>
                        <List dense disablePadding sx={{ bgcolor: theme.palette.action.hover }}>
                          {catItems.map((t) => {
                            const unseen = !seenIds.has(t.id);
                            return (
                              <ListItemButton key={t.id} onClick={() => openTicket(t.id)} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.75, pl: 3 }}>
                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', mt: 0.6, flexShrink: 0, backgroundColor: unseen ? theme.palette.error.main : 'transparent' }} />
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: unseen ? 700 : 500, color: theme.palette.text.primary }} noWrap>
                                    #{t.number} — {t.title || 'Sem título'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }} noWrap component="div">
                                    {[t.client, t.company_code ? `Reg. ${t.company_code}` : null, t.status].filter(Boolean).join(' · ')}
                                  </Typography>
                                </Box>
                                <Tooltip title="Ações" arrow>
                                  <IconButton size="small" edge="end" onClick={(e) => openActionsMenu(e, t)} sx={{ mt: -0.25, flexShrink: 0 }}>
                                    <MoreVertIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </ListItemButton>
                            );
                          })}
                        </List>
                        <Box sx={{ px: 2, py: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                          <Button size="small" endIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />} onClick={() => openInListingFor(c, catItems)} sx={{ textTransform: 'none' }}>
                            Ver na listagem
                          </Button>
                        </Box>
                      </>
                    )}
                  </Collapse>
                  <Divider />
                </Box>
              );
            })
          )}
        </Box>
      </Popover>

      {/* Menu de ações por ticket (item 50): aplicar status / excluir */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeActionsMenu}
        slotProps={{ paper: { sx: { maxHeight: 360, width: 240 } } }}
      >
        {menuTicket && !seenIds.has(menuTicket.id) && (
          <MenuItem
            disabled={busy}
            onClick={() => { const t = menuTicket; closeActionsMenu(); if (t) markSeen(t.id); }}
          >
            <ListItemIcon sx={{ minWidth: 30 }}>
              <VisibilityOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Marcar como lido</ListItemText>
          </MenuItem>
        )}
        <ListSubheader sx={{ lineHeight: '32px', bgcolor: 'transparent' }}>
          Aplicar status
        </ListSubheader>
        {statuses.length === 0 && (
          <MenuItem disabled>
            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Nenhum status</ListItemText>
          </MenuItem>
        )}
        {statuses.map((s) => (
          <MenuItem key={s.id} disabled={busy} onClick={() => applyStatus(s)}>
            <ListItemIcon sx={{ minWidth: 30 }}>
              <LabelOutlinedIcon fontSize="small" sx={{ color: s.color || undefined }} />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{s.name}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          disabled={busy}
          onClick={() => { const t = menuTicket; closeActionsMenu(); setConfirmDelete(t); }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon sx={{ minWidth: 30 }}>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Excluir ticket</ListItemText>
        </MenuItem>
      </Menu>

      {/* Confirmação de exclusão */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => (busy ? null : setConfirmDelete(null))}>
        <DialogTitle>Excluir ticket</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {/*
              * "Não pode ser desfeita" deixou de ser verdade com a lixeira
              * (pontos 8 e 9 da Gisele, 28/10/2026). As duas confirmações de
              * exclusão de chamado foram corrigidas juntas: deixar uma delas
              * para trás daria duas respostas diferentes para a mesma
              * pergunta, dependendo de onde a pessoa clicou.
              */}
            {confirmDelete
              ? `Tem certeza que deseja excluir o ticket #${confirmDelete.number} — ${confirmDelete.title || 'Sem título'}? Ele vai para a lixeira e pode ser restaurado depois.`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={busy} sx={{ textTransform: 'none' }}>
            Cancelar
          </Button>
          <Button onClick={doDelete} color="error" variant="contained" disabled={busy} sx={{ textTransform: 'none' }}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TicketAlertsPanel;
